import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { Resource } from '../../shared/types';
import { apiClient } from '../../services/api/client';
import {
  Search, Command, LayoutDashboard, ListChecks, ScanSearch,
  Share2, Package, Shield, FileText, ShieldCheck, Wrench,
  BarChart3, Settings, ArrowRight, Clock, Zap, Hash,
  Globe, Users, Database, Box, Brain, Bug, AlertTriangle, Route,
} from 'lucide-react';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

interface CommandItem {
  id: string;
  label: string;
  category: 'navigate' | 'action' | 'recent' | 'issues' | 'resources' | 'identities' | 'attack_paths' | 'findings';
  icon: React.ReactNode;
  path?: string;
  action?: () => void;
  shortcut?: string;
  description?: string;
}

// R197 (2026-06-04): backend-search → CommandItem mapping. Wires Cmd-K
// across issues / resources / identities / attack_paths / findings via
// the unified /api/v1/data-plane/search endpoint (gateway-routed to
// dashboard:8010). Wiz signature UX — "jump anywhere".
function _truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + '…' : s;
}

function _itemsFromSearch(groups: any[]): CommandItem[] {
  const out: CommandItem[] = [];
  for (const g of groups || []) {
    const kind: string = g?.kind || '';
    for (const it of (g?.items || []).slice(0, 5)) {
      if (kind === 'issues') {
        out.push({
          id: `iss-${it.id}`,
          category: 'issues',
          icon: <AlertTriangle className="w-4 h-4" />,
          label: _truncate(String(it.title || it.id || 'Issue'), 80),
          description: `${(it.severity || '').toUpperCase()} • ${it.resource_type || ''} ${it.account_id || ''}`.trim(),
          path: `/issues?focus=${encodeURIComponent(it.id)}`,
        });
      } else if (kind === 'resources') {
        out.push({
          id: `res-${it.id}`,
          category: 'resources',
          icon: <Package className="w-4 h-4" />,
          label: _truncate(String(it.name || it.resource_id || 'Resource'), 80),
          description: `${it.resource_type || ''} • ${it.account_id || ''} ${it.region || ''}`.trim(),
          // `/inventory?focus=` was a param NOTHING reads — the result
          // navigated to an unfiltered list, so the jump-to silently did not
          // jump. /resource/:id is the routed detail view and it resolves the
          // same uuid the search returns.
          path: `/resource/${encodeURIComponent(it.id)}`,
        });
      } else if (kind === 'identities') {
        out.push({
          id: `idt-${it.id}`,
          category: 'identities',
          icon: <Users className="w-4 h-4" />,
          label: _truncate(String(it.name || it.id || 'Identity'), 80),
          description: `${it.identity_type || ''} • ${it.provider || ''} ${it.is_admin ? '• admin' : ''}`.trim(),
          path: `/identity/${encodeURIComponent(it.id)}`,
        });
      } else if (kind === 'attack_paths') {
        out.push({
          id: `ap-${it.id}`,
          category: 'attack_paths',
          icon: <Route className="w-4 h-4" />,
          label: _truncate(String(it.name || it.id || 'Attack Path'), 80),
          description: `${(it.severity || '').toUpperCase()} • ${it.hops || it.hop_count || '?'} hops`,
          path: `/attack-paths?path=${encodeURIComponent(it.id)}`,
        });
      } else if (kind === 'findings') {
        out.push({
          id: `fnd-${it.id}`,
          category: 'findings',
          icon: <ScanSearch className="w-4 h-4" />,
          label: _truncate(String(it.title || it.rule_id || 'Finding'), 80),
          description: `${(it.severity || '').toUpperCase()} • ${it.asset_name || it.asset_id || ''}`.trim(),
          path: `/finding/${encodeURIComponent(it.id)}`,
        });
      }
    }
  }
  return out;
}

const NAV_COMMANDS: CommandItem[] = [
  { id: 'nav-dashboard',  label: 'Dashboard',   category: 'navigate', icon: <LayoutDashboard className="w-4 h-4" />, path: '/overview',     description: 'Security overview' },
  { id: 'nav-issues',     label: 'Issues',      category: 'navigate', icon: <ListChecks className="w-4 h-4" />,      path: '/issues',       description: 'Open security issues' },
  { id: 'nav-findings',   label: 'Findings',    category: 'navigate', icon: <ScanSearch className="w-4 h-4" />,      path: '/findings',     description: 'All findings' },
  { id: 'nav-graph',      label: 'Security Graph', category: 'navigate', icon: <Share2 className="w-4 h-4" />,       path: '/graph',        description: 'Resource graph' },
  { id: 'nav-query',      label: 'Ask the Graph',  category: 'navigate', icon: <Share2 className="w-4 h-4" />,       path: '/query',        description: 'Ask the inventory a question in plain language' },
  { id: 'nav-inventory',  label: 'Inventory',   category: 'navigate', icon: <Package className="w-4 h-4" />,         path: '/inventory',    description: 'Cloud resources' },
  { id: 'nav-defend',     label: 'Defend',      category: 'navigate', icon: <Shield className="w-4 h-4" />,          path: '/defend',       description: 'Runtime protection' },
  { id: 'nav-controls',   label: 'Controls',    category: 'navigate', icon: <FileText className="w-4 h-4" />,        path: '/policies',     description: 'Security policies' },
  { id: 'nav-compliance', label: 'Compliance',  category: 'navigate', icon: <ShieldCheck className="w-4 h-4" />,     path: '/compliance',   description: 'Framework compliance' },
  { id: 'nav-actions',    label: 'Actions',     category: 'navigate', icon: <Wrench className="w-4 h-4" />,          path: '/remediation',  description: 'Remediation actions' },
  { id: 'nav-reports',    label: 'Reports',     category: 'navigate', icon: <BarChart3 className="w-4 h-4" />,       path: '/analytics',    description: 'Analytics & reports' },
  { id: 'nav-settings',   label: 'Settings',    category: 'navigate', icon: <Settings className="w-4 h-4" />,        path: '/settings',     description: 'Configuration' },
  { id: 'nav-cspm',       label: 'CSPM',        category: 'navigate', icon: <Shield className="w-4 h-4" />,          path: '/cspm',         description: 'Cloud security posture' },
  { id: 'nav-cwpp',       label: 'CWPP',        category: 'navigate', icon: <Shield className="w-4 h-4" />,          path: '/cwpp',         description: 'Workload protection' },
  { id: 'nav-ciem',       label: 'CIEM',        category: 'navigate', icon: <Users className="w-4 h-4" />,           path: '/ciem',         description: 'Identity entitlements' },
  { id: 'nav-dspm',       label: 'DSPM',        category: 'navigate', icon: <Database className="w-4 h-4" />,        path: '/dspm',         description: 'Data security' },
  { id: 'nav-containers', label: 'Containers',  category: 'navigate', icon: <Box className="w-4 h-4" />,             path: '/container-k8s', description: 'Container security' },
  { id: 'nav-aispm',      label: 'AI-SPM',      category: 'navigate', icon: <Brain className="w-4 h-4" />,           path: '/ai-spm',       description: 'AI security' },
  { id: 'nav-easm',       label: 'EASM',        category: 'navigate', icon: <Globe className="w-4 h-4" />,           path: '/easm',         description: 'Attack surface' },
  { id: 'nav-vulns',      label: 'Vulnerabilities', category: 'navigate', icon: <Bug className="w-4 h-4" />,         path: '/vulnerabilities', description: 'CVE management' },
  { id: 'nav-kspm',       label: 'KSPM',        category: 'navigate', icon: <Box className="w-4 h-4" />,            path: '/kspm',         description: 'Kubernetes posture' },
  { id: 'nav-crown',      label: 'Crown Jewels', category: 'navigate', icon: <Database className="w-4 h-4" />,      path: '/crown-jewels', description: 'Critical assets' },
  { id: 'nav-attackpaths',label: 'Attack Paths', category: 'navigate', icon: <Share2 className="w-4 h-4" />,        path: '/attack-paths', description: 'Toxic attack paths' },
  { id: 'nav-investigations', label: 'Investigations', category: 'navigate', icon: <ScanSearch className="w-4 h-4" />, path: '/investigations', description: 'Threat investigations' },
  { id: 'nav-threatcenter', label: 'Threat Center', category: 'navigate', icon: <Shield className="w-4 h-4" />,     path: '/threat-center', description: 'Threat intelligence' },
  { id: 'nav-detectionrules', label: 'Detection Rules', category: 'navigate', icon: <FileText className="w-4 h-4" />, path: '/detection-rules', description: 'CDR detection rules' },
  { id: 'nav-workflows',  label: 'Workflows',   category: 'navigate', icon: <Wrench className="w-4 h-4" />,         path: '/workflows',    description: 'Automation workflows' },
  { id: 'nav-codetocloud',label: 'Code to Cloud', category: 'navigate', icon: <FileText className="w-4 h-4" />,     path: '/code-to-cloud', description: 'Code-to-cloud tracing' },
];

const CATEGORY_LABELS: Record<string, string> = {
  recent: 'Recent',
  navigate: 'Navigate',
  action: 'Actions',
  issues: 'Issues',
  resources: 'Resources',
  identities: 'Identities',
  attack_paths: 'Attack Paths',
  findings: 'Findings',
};

export default function CommandPalette({ open, onClose, onNavigate }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchResults, setSearchResults] = useState<CommandItem[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Load recent navigation from localStorage
  const recentPaths = useMemo(() => {
    try {
      const stored = localStorage.getItem('cmd_palette_recent');
      return stored ? JSON.parse(stored).slice(0, 5) : [];
    } catch { return []; }
  }, [open]);

  const recentItems: CommandItem[] = recentPaths
    .map((path: string) => NAV_COMMANDS.find(c => c.path === path))
    .filter(Boolean)
    .map((c: any) => ({ ...c, id: `recent-${c.id}`, category: 'recent' as const }));

  // R197 (2026-06-04): debounced backend search across issues / resources /
  // identities / attack_paths / findings. 250ms debounce — avoids hammering
  // the gateway as the user types. /api/v1/data-plane/search fans out to
  // five canonical services on the BE side; quiet 5xx → graceful empty.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data, error } = await apiClient.get<any>(
          `/api/v1/data-plane/search?q=${encodeURIComponent(q)}&limit=5`,
          undefined,
          ctrl.signal,
        );
        if (!error && data && Array.isArray(data.groups)) {
          setSearchResults(_itemsFromSearch(data.groups));
        } else {
          setSearchResults([]);
        }
      } catch { /* aborted or transient — keep prior */ }
      finally { setSearching(false); }
    }, 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query]);

  const filteredItems = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) {
      return [...recentItems, ...NAV_COMMANDS];
    }
    const nav = NAV_COMMANDS.filter(
      item =>
        item.label.toLowerCase().includes(q) ||
        (item.description && item.description.toLowerCase().includes(q))
    );
    // Nav matches first (jump-to-page is what most Cmd-K users want),
    // then backend results grouped by kind.
    return [...nav, ...searchResults];
  }, [query, recentItems, searchResults]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Global keyboard shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (open) onClose();
        else onClose(); // toggle handled by parent
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const executeItem = useCallback((item: CommandItem) => {
    if (item.path) {
      // Save to recent
      const recent = recentPaths.filter((p: string) => p !== item.path);
      recent.unshift(item.path);
      localStorage.setItem('cmd_palette_recent', JSON.stringify(recent.slice(0, 10)));
      onNavigate(item.path);
    }
    if (item.action) item.action();
    onClose();
  }, [onClose, onNavigate, recentPaths]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, filteredItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredItems[selectedIndex]) executeItem(filteredItems[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!open) return null;

  // Group items by category
  const groups: { category: string; items: CommandItem[] }[] = [];
  const seen = new Set<string>();
  filteredItems.forEach(item => {
    if (!seen.has(item.category)) {
      seen.add(item.category);
      groups.push({ category: item.category, items: [] });
    }
    groups.find(g => g.category === item.category)?.items.push(item);
  });

  let globalIndex = 0;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={onClose} aria-hidden="true" />

      {/* Palette */}
      <div
        className="fixed top-[15%] left-1/2 -translate-x-1/2 w-[560px] max-w-[90vw] bg-wiz-dark border border-wiz-border rounded-2xl shadow-2xl z-50 overflow-hidden animate-modal-in"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-wiz-border">
          <Search className="w-5 h-5 text-gray-500 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, actions, resources..."
            className="flex-1 bg-transparent text-gray-200 text-sm placeholder-gray-600 outline-none"
            aria-label="Search commands"
            autoComplete="off"
          />
          <kbd className="text-xs text-gray-600 bg-wiz-card border border-wiz-border rounded px-1.5 py-0.5 font-security shrink-0">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[400px] overflow-y-auto py-2">
          {filteredItems.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 text-sm">
              {searching ? 'Searching…' : `No results for "${query}"`}
            </div>
          ) : (
            groups.map(group => (
              <div key={group.category}>
                <div className="px-4 py-1.5">
                  <span className="section-label">{CATEGORY_LABELS[group.category] || group.category}</span>
                </div>
                {group.items.map(item => {
                  const idx = globalIndex++;
                  const isSelected = idx === selectedIndex;
                  return (
                    <button
                      key={item.id}
                      data-index={idx}
                      onClick={() => executeItem(item)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      className={`
                        w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors
                        ${isSelected ? 'bg-wiz-purple/10 text-wiz-purple' : 'text-gray-400 hover:bg-wiz-card/60'}
                      `}
                    >
                      <span className={`shrink-0 ${isSelected ? 'text-wiz-purple' : 'text-gray-500'}`}>
                        {item.category === 'recent' ? <Clock className="w-4 h-4" /> : item.icon}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className={`text-sm font-medium ${isSelected ? 'text-gray-200' : 'text-gray-300'}`}>
                          {item.label}
                        </span>
                        {item.description && (
                          <span className="text-xs text-gray-600 ml-2">{item.description}</span>
                        )}
                      </span>
                      {item.shortcut && (
                        <kbd className="text-xs text-gray-600 bg-wiz-card border border-wiz-border rounded px-1.5 py-0.5 font-security">
                          {item.shortcut}
                        </kbd>
                      )}
                      {isSelected && <ArrowRight className="w-3.5 h-3.5 text-wiz-purple shrink-0" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-2.5 border-t border-wiz-border flex items-center gap-4 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <kbd className="bg-wiz-card border border-wiz-border rounded px-1 py-0.5 font-security">↑↓</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="bg-wiz-card border border-wiz-border rounded px-1 py-0.5 font-security">↵</kbd>
            select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="bg-wiz-card border border-wiz-border rounded px-1 py-0.5 font-security">esc</kbd>
            close
          </span>
        </div>
      </div>
    </>
  );
}
